import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Stack,
  Grid,
  Paper,
  Divider,
  Checkbox,
  FormControlLabel,
  TextField,
  Button,
  Alert,
  useMediaQuery,
  MenuItem,
  Dialog as MuiDialog,
  DialogContent as MuiDialogContent,
  DialogActions as MuiDialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { alpha, useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";

import UserFormFields from "./UserFormFields";
import {
  subAdminPermissionsSchema,
  defaultSubAdminPermissions,
  defaultPreferences,
  normalizeUserId,
} from "./permissionsDefaults";
import {
  useCreateUserMutation,
  useEditUserMutation,
} from "../../redux/admin/adminApi";

const Card = ({ title, children }) => (
  <Paper
    variant="outlined"
    sx={{
      p: { xs: 1.5, sm: 2 },
      borderRadius: 2,
      bgcolor: "background.paper",
    }}
  >
    <Stack spacing={1.25}>
      <Typography variant="subtitle1" fontWeight={800}>
        {title}
      </Typography>
      <Divider />
      {children}
    </Stack>
  </Paper>
);

const CollapsibleSection = ({ title, subtitle, expanded, onChange, children, defaultExpanded = false }) => (
  <Accordion
    disableGutters
    expanded={expanded}
    onChange={onChange}
    defaultExpanded={defaultExpanded}
    sx={(t) => ({
      borderRadius: 2,
      overflow: "hidden",
      border: `1px solid ${alpha(t.palette.divider, 0.85)}`,
      boxShadow: "none",
      "&:before": { display: "none" },
      bgcolor: alpha(t.palette.common.white, 0.98),
    })}
  >
    <AccordionSummary
      expandIcon={<ExpandMoreIcon sx={{ color: "text.secondary" }} />}
      sx={(t) => ({
        minHeight: 48,
        px: 1.5,
        py: 0.25,
        bgcolor: alpha(t.palette.common.black, 0.03),
        borderBottom: expanded ? `1px solid ${alpha(t.palette.divider, 0.7)}` : "none",
        "& .MuiAccordionSummary-content": {
          my: 0.5,
        },
      })}
    >
      <Box>
        <Typography fontWeight={800} fontSize="0.96rem">
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        ) : null}
      </Box>
    </AccordionSummary>
    <AccordionDetails sx={{ p: { xs: 1.5, sm: 2 } }}>{children}</AccordionDetails>
  </Accordion>
);

const setDeep = (obj, path, value) => {
  const keys = path.split(".");
  const out = structuredClone(obj);
  let cur = out;
  for (let i = 0; i < keys.length - 1; i += 1) {
    cur[keys[i]] = cur[keys[i]] ?? {};
    cur = cur[keys[i]];
  }
  cur[keys[keys.length - 1]] = value;
  return out;
};

const ATTRIBUTE_OPTIONS = [
  { key: "notificationTokens", label: "Notification Tokens", type: "number" },
  { key: "currency", label: "Currency", type: "text" },
  { key: "unitPrice", label: "Unit Price", type: "number" },
  { key: "managerEmailAddress", label: "Manager Email Address", type: "email" },
  { key: "managerContactNumber", label: "Manager Contact Number", type: "tel" },
  { key: "remarks", label: "Remarks", type: "text" },
  {
    key: "roleAttribute",
    label: "Role",
    type: "select",
    options: ["Parents", "Subuser", "Relay hide"],
  },
  { key: "video", label: "Video", type: "checkbox" },
  { key: "useExternalMap", label: "Use external map", type: "checkbox" },
  { key: "mapKey", label: "Map key", type: "text" },
  { key: "razorpayKey", label: "Razorpay Key", type: "text" },
];

const ATTRIBUTE_MAP = ATTRIBUTE_OPTIONS.reduce((acc, item) => {
  acc[item.key] = item;
  return acc;
}, {});

const buildParentAllowedForSubAdminSchema = (currentUser) => {
  const nestedPermissions = currentUser?.permissions;
  if (
    nestedPermissions &&
    typeof nestedPermissions === "object" &&
    (nestedPermissions.device || nestedPermissions.user || nestedPermissions.dealer)
  ) {
    return currentUser.permissions;
  }

  const dp = currentUser?.devicePermissions || {};
  const up = currentUser?.userPermissions || {};
  const app = currentUser?.permissions?.app || {};

  return {
    device: {
      add: dp.create === true || app.createDevice === true,
      edit: dp.update === true || app.editDevice === true,
      delete: dp.delete === true || app.deleteDevice === true,
      disable: dp.update === true || app.editDevice === true,
      renew: dp.update === true || app.editDevice === true,
    },
    user: {
      add: up.create === true || app.createUser === true,
      dealer: up.assign === true || app.assignUserToGA === true || app.createUser === true,
      edit: up.update === true || app.editUser === true,
      delete: up.delete === true || app.deleteUser === true,
    },
    dealer: {
      add: up.create === true || app.createUser === true,
      edit: up.update === true || app.editUser === true,
      delete: up.delete === true || app.deleteUser === true,
      renew: dp.update === true || app.editUser === true || app.editDevice === true,
      assign:
        up.assign !== undefined
          ? up.assign === true
          : app.assignUserToGA === true || app.createUser === true,
    },
    notification: { add: app.messageManagement === true || app.createUser === true || app.editUser === true },
    dataPush: { allow: app.createUser === true || app.editUser === true },
    sensors: { allow: app.manageSensors === true || app.editDevice === true || app.createUser === true },
    summaryEdit: { allow: app.viewDashboard === true || app.editUser === true || app.createUser === true },
    deviceTextbox: { allow: app.createDevice === true || app.editDevice === true || app.createUser === true },
    other: {
      disabled: app.editUser === true || app.createUser === true,
      limitCommands: dp.command !== undefined ? dp.command === true : app.sendCommands === true,
      readonly: app.viewDevices === true || app.viewUsers === true,
      disableReports: app.viewDashboard === true || app.viewMap === true || app.createUser === true,
      dontUseDealerLicense: app.createUser === true,
      deviceReadonly: app.viewDevices === true || app.createUser === true,
      stopParkingAlerts: app.createUser === true,
    },
    limits: {
      deviceLimit: true,
      userLimit: true,
    },
  };
};

const clampBySchema = (schema, parentAllowed, selected) => {
  const out = structuredClone(selected || {});
  schema.forEach((group) => {
    out[group.key] = out[group.key] ?? {};
    const parentGroup = parentAllowed?.[group.key] || {};
    group.items.forEach((it) => {
      const isAllowed = parentGroup?.[it.key] === true;
      out[group.key][it.key] = isAllowed ? Boolean(out[group.key]?.[it.key]) : false;
    });
  });

  out.limits = out.limits ?? {};
  out.limits.deviceLimit =
    parentAllowed?.limits?.deviceLimit === true ? Number(out.limits.deviceLimit || 0) : 0;
  out.limits.userLimit =
    parentAllowed?.limits?.userLimit === true ? Number(out.limits.userLimit || 0) : 0;

  return out;
};

const mapLegacySubAdminPermissions = (user) => ({
  device: {
    add: user?.devicePermissions?.create === true,
    disable:
      user?.devicePermissions?.update === true &&
      user?.permissions?.device?.disable === true,
    edit: user?.devicePermissions?.update === true,
    delete: user?.devicePermissions?.delete === true,
    renew: user?.permissions?.device?.renew === true,
  },
  user: {
    add: user?.userPermissions?.create === true,
    dealer:
      user?.permissions?.user?.dealer === true || user?.userPermissions?.assign === true,
    edit: user?.userPermissions?.update === true,
    delete: user?.userPermissions?.delete === true,
  },
  dealer: {
    add: user?.permissions?.dealer?.add === true || user?.userPermissions?.create === true,
    edit: user?.permissions?.dealer?.edit === true || user?.userPermissions?.update === true,
    delete:
      user?.permissions?.dealer?.delete === true || user?.userPermissions?.delete === true,
    renew: user?.permissions?.dealer?.renew === true,
    assign:
      user?.permissions?.dealer?.assign === true || user?.userPermissions?.assign === true,
  },
  notification: { add: user?.permissions?.notification?.add === true },
  dataPush: { allow: user?.permissions?.dataPush?.allow === true },
  sensors: { allow: user?.permissions?.sensors?.allow === true },
  summaryEdit: { allow: user?.permissions?.summaryEdit?.allow === true },
  deviceTextbox: { allow: user?.permissions?.deviceTextbox?.allow === true },
  other: {
    disabled: user?.permissions?.other?.disabled === true,
    limitCommands:
      user?.permissions?.other?.limitCommands === true ||
      user?.devicePermissions?.command === false,
    readonly: user?.permissions?.other?.readonly === true,
    disableReports: user?.permissions?.other?.disableReports === true,
    dontUseDealerLicense: user?.permissions?.other?.dontUseDealerLicense === true,
    deviceReadonly: user?.permissions?.other?.deviceReadonly === true,
    stopParkingAlerts: user?.permissions?.other?.stopParkingAlerts === true,
  },
  limits: {
    deviceLimit: Number(user?.permissions?.limits?.deviceLimit || 0),
    userLimit: Number(user?.permissions?.limits?.userLimit || 0),
  },
});

const toSubAdminFormState = (user) => {
  if (!user) {
    return {
      username: "",
      email: "",
      mobileNo: "",
      password: "",
      uniqueId: "",
      preferences: structuredClone(defaultPreferences),
      permissions: structuredClone(defaultSubAdminPermissions),
      attributes: [],
    };
  }

  return {
    username: user.username || "",
    email: user.email || "",
    mobileNo: user.mobileNo || "",
    password: "",
    uniqueId: user.uniqueId || "",
    preferences: {
      ...defaultPreferences,
      ...(user.preferences || {}),
      timeZone:
        user.preferences?.timeZone ||
        user.preferences?.timezone ||
        defaultPreferences.timeZone,
      language: user.preferences?.language || defaultPreferences.language,
    },
    permissions: user.permissions
      ? { ...structuredClone(defaultSubAdminPermissions), ...structuredClone(user.permissions) }
      : mapLegacySubAdminPermissions(user),
    attributes: Array.isArray(user.attributes) ? structuredClone(user.attributes) : [],
  };
};

export default function AddSubAdminModal({
  open,
  onClose,
  onSuccess,
  mode = "create",
  initialData = null,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [createUser, { isLoading }] = useCreateUserMutation();
  const [editUser, { isLoading: isEditing }] = useEditUserMutation();

  const currentUser = useSelector((s) => s?.auth?.user);
  const parentAllowed = useMemo(
    () => buildParentAllowedForSubAdminSchema(currentUser),
    [currentUser]
  );

  const [error, setError] = useState("");
  const [form, setForm] = useState(() => toSubAdminFormState(initialData));
  const [attributeDialogOpen, setAttributeDialogOpen] = useState(false);
  const [selectedAttributeKey, setSelectedAttributeKey] = useState("");
  const [expandedSections, setExpandedSections] = useState({
    details: true,
    permissions: false,
    attributes: false,
  });

  const header = useMemo(
    () => ({
      title: mode === "edit" ? "Edit Admin" : "Add Admin",
      subtitle:
        mode === "edit"
          ? "Update admin information, permissions and attributes"
          : "Create admin account with full hierarchy permissions",
    }),
    [mode]
  );

  useEffect(() => {
    if (!open) return;
    setForm(toSubAdminFormState(initialData));
    setError("");
    setSelectedAttributeKey("");
    setAttributeDialogOpen(false);
    setExpandedSections({
      details: true,
      permissions: false,
      attributes: false,
    });
  }, [open, initialData]);

  const toggleSection = (key) => (_, nextExpanded) => {
    setExpandedSections((prev) => ({ ...prev, [key]: nextExpanded }));
  };

  const onChange = (path, value) => {
    setError("");
    if (path === "uniqueId") value = normalizeUserId(value);
    setForm((prev) => setDeep(prev, path, value));
  };

  const togglePermission = (groupKey, itemKey) => {
    setError("");
    const allowed = parentAllowed?.[groupKey]?.[itemKey] === true;
    if (!allowed) return;

    setForm((prev) => {
      const current = !!prev.permissions?.[groupKey]?.[itemKey];
      return setDeep(prev, `permissions.${groupKey}.${itemKey}`, !current);
    });
  };

  const availableAttributeOptions = ATTRIBUTE_OPTIONS.filter(
    (item) => !(form.attributes || []).some((attr) => attr.key === item.key)
  );

  const addAttribute = () => {
    if (!selectedAttributeKey) return;
    const selectedConfig = ATTRIBUTE_MAP[selectedAttributeKey];
    if (!selectedConfig) return;

    setForm((prev) => ({
      ...prev,
      attributes: [
        ...(prev.attributes || []),
        {
          key: selectedConfig.key,
          label: selectedConfig.label,
          type: selectedConfig.type,
          value: selectedConfig.type === "checkbox" ? false : "",
        },
      ],
    }));
    setSelectedAttributeKey("");
    setAttributeDialogOpen(false);
  };

  const updateAttribute = (index, value) => {
    setForm((prev) => {
      const nextAttributes = [...(prev.attributes || [])];
      nextAttributes[index] = { ...nextAttributes[index], value };
      return { ...prev, attributes: nextAttributes };
    });
  };

  const removeAttribute = (index) => {
    setForm((prev) => ({
      ...prev,
      attributes: (prev.attributes || []).filter((_, attrIndex) => attrIndex !== index),
    }));
  };

  const handleSubmit = async () => {
    setError("");

    if (!form.username?.trim()) return setError("Username is required");
    if (!form.email?.trim()) return setError("Email is required");
    if (mode !== "edit" && !form.password?.trim()) return setError("Password is required");

    const clampedPermissions = clampBySchema(
      subAdminPermissionsSchema,
      parentAllowed,
      form.permissions
    );

    const payload = {
      username: form.username.trim(),
      email: form.email.trim().toLowerCase(),
      mobileNo: String(form.mobileNo || "").trim(),
      ...(form.password?.trim() ? { password: form.password } : {}),
      role: "sub_admin",
      uniqueId: form.uniqueId ? normalizeUserId(form.uniqueId) : undefined,
      preferences: form.preferences,
      permissions: clampedPermissions,
      attributes: form.attributes,
    };

    try {
      const res =
        mode === "edit" && initialData?._id
          ? await editUser({ userId: initialData._id, ...payload }).unwrap()
          : await createUser(payload).unwrap();

      onSuccess?.(res);
      onClose?.();

      setForm(toSubAdminFormState(null));
      setSelectedAttributeKey("");
      setAttributeDialogOpen(false);
    } catch (e) {
      setError(
        e?.data?.msg ||
          e?.message ||
          (mode === "edit" ? "Failed to update admin" : "Failed to create admin")
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 3,
          overflow: "hidden",
          boxShadow: `0 16px 44px ${alpha(theme.palette.common.black, 0.2)}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "background.paper",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={900}>
            {header.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {header.subtitle}
          </Typography>
        </Box>

        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, sm: 3 }, bgcolor: "grey.50" }}>
        <Stack spacing={2.25}>
          {error ? <Alert severity="error">{error}</Alert> : null}

          <CollapsibleSection
            title="Admin details"
            subtitle="Account information and preferences"
            expanded={expandedSections.details}
            onChange={toggleSection("details")}
            defaultExpanded
          >
            <UserFormFields form={form} onChange={onChange} requirePassword={mode !== "edit"} />
          </CollapsibleSection>

          <CollapsibleSection
            title="Permissions"
            subtitle="Parent-limited admin permission groups"
            expanded={expandedSections.permissions}
            onChange={toggleSection("permissions")}
          >
            <Card title="Permissions (Parent Limited)">
              <Grid container spacing={2}>
                {subAdminPermissionsSchema.map((group) => (
                  <Grid key={group.key} item xs={12} md={group.key === "other" ? 12 : 6}>
                    <Paper
                      variant="outlined"
                      sx={{
                        borderRadius: 2,
                        p: 1.5,
                        height: "100%",
                        bgcolor: "background.paper",
                      }}
                    >
                      <Typography fontWeight={800} sx={{ mb: 1 }}>
                        {group.title}
                      </Typography>

                      <Grid container spacing={1}>
                        {group.items.map((it) => {
                          const allowed = parentAllowed?.[group.key]?.[it.key] === true;
                          const checked = !!form.permissions?.[group.key]?.[it.key];

                          return (
                            <Grid key={it.key} item xs={12} sm={6}>
                              <FormControlLabel
                                disabled={!allowed}
                                control={
                                  <Checkbox
                                    checked={allowed ? checked : false}
                                    onChange={() => togglePermission(group.key, it.key)}
                                  />
                                }
                                label={
                                  <Typography
                                    variant="body2"
                                    sx={{ userSelect: "none" }}
                                    color={!allowed ? "text.disabled" : "text.primary"}
                                  >
                                    {it.label}
                                  </Typography>
                                }
                                sx={{ alignItems: "flex-start", m: 0 }}
                              />
                            </Grid>
                          );
                        })}
                      </Grid>

                      {group.key === "other" ? (
                        <Box sx={{ mt: 2 }}>
                          <Divider sx={{ mb: 1.5 }} />
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label="Device Limit"
                                value={form.permissions?.limits?.deviceLimit ?? 0}
                                onChange={(e) =>
                                  onChange("permissions.limits.deviceLimit", Number(e.target.value || 0))
                                }
                                fullWidth
                                size="small"
                                type="number"
                                inputProps={{ min: 0 }}
                                disabled={parentAllowed?.limits?.deviceLimit !== true}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label="User Limit"
                                value={form.permissions?.limits?.userLimit ?? 0}
                                onChange={(e) =>
                                  onChange("permissions.limits.userLimit", Number(e.target.value || 0))
                                }
                                fullWidth
                                size="small"
                                type="number"
                                inputProps={{ min: 0 }}
                                disabled={parentAllowed?.limits?.userLimit !== true}
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      ) : null}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Card>
          </CollapsibleSection>

          <CollapsibleSection
            title="Attributes"
            subtitle={`${(form.attributes || []).length} attributes added`}
            expanded={expandedSections.attributes}
            onChange={toggleSection("attributes")}
          >
            <Card title="Attributes">
              <Stack spacing={1.5}>
                {(form.attributes || []).map((attr, index) => {
                  const config = ATTRIBUTE_MAP[attr.key] || attr;

                  return (
                    <Stack key={attr.key} direction="row" spacing={1.25} alignItems="flex-start">
                      <Box sx={{ flex: 1 }}>
                        {config.type === "checkbox" ? (
                          <FormControlLabel
                            sx={{ m: 0, mb: 0.5 }}
                            control={
                              <Checkbox
                                checked={Boolean(attr.value)}
                                onChange={(e) => updateAttribute(index, e.target.checked)}
                              />
                            }
                            label={config.label || attr.key}
                          />
                        ) : (
                          <Typography sx={{ mb: 0.75, fontWeight: 500 }}>
                            {config.label || attr.key}
                          </Typography>
                        )}

                        {config.type === "select" ? (
                          <TextField
                            select
                            value={attr.value || ""}
                            onChange={(e) => updateAttribute(index, e.target.value)}
                            size="small"
                            fullWidth
                          >
                            {(config.options || []).map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </TextField>
                        ) : config.type === "checkbox" ? null : (
                          <TextField
                            type={config.type || "text"}
                            value={attr.value || ""}
                            onChange={(e) => updateAttribute(index, e.target.value)}
                            size="small"
                            fullWidth
                          />
                        )}
                      </Box>

                      <IconButton color="error" onClick={() => removeAttribute(index)} size="small">
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Stack>
                  );
                })}

                <Button
                  variant="outlined"
                  onClick={() => setAttributeDialogOpen(true)}
                  disabled={!availableAttributeOptions.length}
                  sx={{ fontWeight: 700, py: 1.1 }}
                >
                  + ADD
                </Button>
              </Stack>
            </Card>
          </CollapsibleSection>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Admin role: <b>sub_admin</b>
        </Typography>

        <Stack direction="row" spacing={1.25}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isLoading || isEditing}
            sx={{ minWidth: 140, fontWeight: 800 }}
          >
            {isLoading || isEditing ? "Saving..." : mode === "edit" ? "Update" : "Save"}
          </Button>
        </Stack>
      </DialogActions>

      <MuiDialog
        open={attributeDialogOpen}
        onClose={() => setAttributeDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <MuiDialogContent sx={{ pt: 2 }}>
          <Stack spacing={1.5}>
            <TextField
              select
              label="Attribute"
              value={selectedAttributeKey}
              onChange={(e) => setSelectedAttributeKey(e.target.value)}
              size="small"
              fullWidth
            >
              {availableAttributeOptions.map((item) => (
                <MenuItem key={item.key} value={item.key}>
                  {item.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Type"
              value={selectedAttributeKey ? ATTRIBUTE_MAP[selectedAttributeKey]?.type || "" : ""}
              size="small"
              fullWidth
              InputProps={{ readOnly: true }}
            />
          </Stack>
        </MuiDialogContent>

        <MuiDialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={addAttribute} disabled={!selectedAttributeKey}>
            Add
          </Button>
          <Button onClick={() => setAttributeDialogOpen(false)}>Cancel</Button>
        </MuiDialogActions>
      </MuiDialog>
    </Dialog>
  );
}
